/**
 * RouteErrorBoundary — 2026-02-11 (audit P0-1)
 * Route-level error boundary that renders inside the authenticated shell
 * (sidebar + header intact) when a child page throws. Prevents blank white
 * screens by turning runtime errors into a visible retryable error card.
 */
import React from 'react';

export class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error('[RouteErrorBoundary]', error, errorInfo);
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }
    const message = this.state.error?.message || 'An unexpected error occurred';
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="route-error-boundary">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
          <div className="w-12 h-12 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-4">
            <span className="text-red-600 text-xl font-bold">!</span>
          </div>
          <h1 className="text-xl font-semibold text-slate-900 mb-1">This page hit an error</h1>
          <p className="text-sm text-slate-500 mb-4">{message}</p>
          <div className="flex gap-2 justify-center">
            <button
              type="button"
              onClick={this.reset}
              className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium"
              data-testid="route-error-boundary-retry"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => { window.location.href = '/dashboard'; }}
              className="px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium"
              data-testid="route-error-boundary-dashboard"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default RouteErrorBoundary;
